import styled from '@emotion/styled';
import { Region } from '@kaizenplatform/hugot-entity';
import { Button, Dialog, DialogContent, DialogContentText, DialogTitle, Typography } from '@material-ui/core';
import _NavigateNext from '@material-ui/icons/NavigateNext';
import { ClassAttributes, createElement, MouseEvent, ReactType } from 'react';
import { FormattedMessage } from 'react-intl';

import { File } from '../../../generated/graphql';
import {
  isCreativeAbleToSendToAmazon,
  isCreativeAbleToSendToFacebook,
  isCreativeAbleToSendToGoogle,
  isCreativeAbleToSendToTiktok,
  isCreativeAbleToSendToYahoo,
} from '../../../utility/isCreativeAbleToSend';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import AmazonIcon from '../primitive/AmazonIcon';
import FacebookIconWithOriginalColor from '../primitive/FacebookIconWithOriginalColor';
import InstagramIcon from '../primitive/InstagramIcon';
import TiktokIcon from '../primitive/TiktokIcon';
import YahooIcon from '../primitive/YahooIcon';
import YouTubeIcon from '../primitive/YouTubeIcon';

export const isAdPlatformSelectable = (adPlatform: string, creative: { file: Pick<File, 'size' | 'mimeType'> }) => {
  if (adPlatform === 'facebook') {
    return isCreativeAbleToSendToFacebook(creative);
  } else if (adPlatform === 'google') {
    return isCreativeAbleToSendToGoogle(creative);
  } else if (adPlatform === 'amazon') {
    return isCreativeAbleToSendToAmazon(creative);
  } else if (adPlatform === 'yahoo') {
    return isCreativeAbleToSendToYahoo(creative);
  } else if (adPlatform === 'tiktok') {
    return isCreativeAbleToSendToTiktok(creative);
  }
  return false;
};

const selectableAdPlatforms: Array<{
  key: string;
  name: string;
  adMediaIcons: Array<ReactType<{ size: number }>>;
}> = [
  {
    key: 'facebook',
    name: 'Facebook',
    adMediaIcons: [FacebookIconWithOriginalColor, InstagramIcon],
  },
  {
    key: 'google',
    name: 'Google',
    adMediaIcons: [YouTubeIcon],
  },
  {
    key: 'amazon',
    name: 'amazon',
    adMediaIcons: [AmazonIcon],
  },
  {
    key: 'yahoo',
    name: 'yahoo',
    adMediaIcons: [YahooIcon],
  },
  {
    key: 'tiktok',
    name: 'TikTok',
    adMediaIcons: [TiktokIcon],
  },
];

type Props = ClassAttributes<HTMLElement> & {
  creative: { file: Pick<File, 'size' | 'mimeType'> };
  region: Region;
  uploadedAdPlatforms?: Array<string>;
  open: boolean;
  onRequestSelectingNetwork: (e: MouseEvent<HTMLElement>, adPlatform: string) => void;
  onRequestClose: () => void;
  isUploadedToAmazon: boolean;
  isUploadedToYahoo: boolean;
  isUploadedToTiktok: boolean;
  className?: string;
};

const CreativeSendingAdPlatformSelectorDialog = ({
  creative,
  region,
  uploadedAdPlatforms = [],
  open,
  onRequestSelectingNetwork,
  onRequestClose,
  isUploadedToAmazon,
  isUploadedToYahoo,
  isUploadedToTiktok,
  className,
}: Props) => {
  return (
    <Dialog open={open} onClose={onRequestClose} className={className} maxWidth="md">
      <DialogTitle disableTypography>
        <Typography variant="h6">
          <FormattedMessage id="creative_sending_ad_platform_selector_dialog.title" />
        </Typography>
        <Description variant="body2">
          <FormattedMessage id="creative_sending_ad_platform_selector_dialog.description" />
        </Description>
      </DialogTitle>

      <DialogContent>
        <AdPlatformsHeader variant="body2">
          <FormattedMessage id="creative_sending_ad_platform_selector_dialog.ad_platforms_header" />
        </AdPlatformsHeader>
        <AdPlatforms>
          {selectableAdPlatforms
            .filter((adPlatform) => !(region !== 'jp' && adPlatform.key === 'yahoo'))
            .map((adPlatform) => (
              <AdPlatformButton
                color="default"
                key={`${adPlatform.key}`}
                onClick={(e) => onRequestSelectingNetwork(e, adPlatform.key)}
                disabled={!isAdPlatformSelectable(adPlatform.key, creative)}
              >
                <AdPlatformWithMediaIcons>
                  <AdPlatformName variant="body1">{adPlatform.name}</AdPlatformName>
                  <Icons>
                    {adPlatform.adMediaIcons.map((Icon, index) => (
                      <Icon key={`${index}`} size={24} />
                    ))}
                  </Icons>
                  <Spacer />

                  {(uploadedAdPlatforms.includes(adPlatform.key) ||
                    (adPlatform.key === 'amazon' && isUploadedToAmazon) ||
                    (adPlatform.key === 'yahoo' && isUploadedToYahoo) ||
                    (adPlatform.key === 'tiktok' && isUploadedToTiktok)) && (
                    <Status variant="caption">
                      <FormattedMessage id="creative_sending_ad_platform_selector_dialog.ad_platforms.already_uploaded" />
                    </Status>
                  )}

                  <NavigateNext color="primary" />
                </AdPlatformWithMediaIcons>
              </AdPlatformButton>
            ))}
        </AdPlatforms>
      </DialogContent>
    </Dialog>
  );
};

const Description = styled(DialogContentText)({
  marginTop: SPACING_REGULAR,
});

const AdPlatformsHeader = styled(Typography)({
  marginBottom: SPACING_UNIT,
});

const AdPlatforms = styled('div')({
  marginBottom: SPACING_LARGE,
});

const AdPlatformButton = styled(Button)({
  width: '100%',
  height: 48,
  textTransform: 'none',
  '&:not(:last-child)': {
    marginBottom: SPACING_UNIT * 2,
  },
  [mq('sp')]: {
    height: 'auto',
  },
});
AdPlatformButton.defaultProps = { variant: 'outlined' };

const AdPlatformWithMediaIcons = styled('div')(
  gridLayoutContainer({
    columns: ['128px', 'auto', '1fr', 'auto', '1em'],
    rows: ['auto'],
    areas: [['name', 'icons', 'spacer', 'status', 'arrow']],
    alignItems: 'center',
    justifyContent: 'start',
  }),
  {
    textTransform: 'none',
    width: '100%',
    '& > *:not(:last-child)': {
      marginRight: SPACING_UNIT,
    },
    [mq('sp')]: {
      ...gridLayoutContainer({
        columns: ['auto', '1fr', '1em'],
        rows: ['auto', 'auto'],
        areas: [
          ['name', 'icons', 'arrow'],
          ['status', 'status', 'arrow'],
        ],
      }),
    },
  },
);

const AdPlatformName = styled(Typography)({
  ...gridLayoutItem('name', {
    justifySelf: 'start',
  }),
  marginRight: SPACING_UNIT,
});

const Icons = styled('div')(
  {
    ...gridLayoutItem('icons', {
      justifySelf: 'start',
      alignSelf: 'center',
    }),
  },
  {
    height: 24,
    '& > *:not(:last-child)': {
      marginRight: SPACING_UNIT,
    },
  },
);

const Spacer = styled('div')({
  ...gridLayoutItem('spacer'),
});

const Status = styled(Typography)({
  ...gridLayoutItem('status', {
    justifySelf: 'start',
  }),
  [mq('sp')]: {
    marginTop: 4,
  },
});

const NavigateNext = styled(_NavigateNext)({
  ...gridLayoutItem('arrow'),
});

export default CreativeSendingAdPlatformSelectorDialog;
